import { FC, useEffect } from 'react'
import MediaGallery from 'components/MediaGallery'
import useContainerWidth from 'hooks/useContainerWidth'
import {
  isUploadReady,
  MediaUpload,
  uploadsProgressSlice,
} from '@wellesley/uploads'
import SingleMedia from './SingleMedia'
import MultipleMedia from './MultipleMedia'
import { MediaItemProps } from 'components/MediaItem'
import ContentWarningWrapper from './ContentWarningWrapper'
import { useSelector, useDispatch } from 'react-redux'
import Alert from 'components/Alert'

type MediaContentProps = {
  containerWidth?: number
  attachments: MediaUpload[]
  isReply?: boolean
  summary?: string | null
  isSmallPreview?: boolean
} & Pick<MediaItemProps, 'removeMedia' | 'updateMedia'>

const MediaContent: FC<MediaContentProps> = ({
  containerWidth,
  summary,
  isReply,
  isSmallPreview,
  attachments: allAttachments,
  ...editProps
}) => {
  const [ref, mediaContentWidth] = useContainerWidth()
  const dispatch = useDispatch()

  const attachments = allAttachments.filter((u) => isUploadReady(u))
  const hasError = useSelector(uploadsProgressSlice.selectors.selectHasError)

  useEffect(() => {
    return () => {
      dispatch(uploadsProgressSlice.actions.resetProgress())
    }
  }, [dispatch])

  if (!attachments.length) return null

  const isSingleMedia = attachments.length === 1

  const width = containerWidth || mediaContentWidth

  return (
    <div ref={ref}>
      {hasError && (
        <Alert type="error" style="filled" className="mb-xs">
          The post cannot be posted until the media issues is fixed. Please
          check the error details and upload another file
        </Alert>
      )}
      {!!width && (
        <MediaGallery uploads={attachments}>
          {(openGallery) => (
            <ContentWarningWrapper
              summary={summary}
              attachments={attachments}
              containerWidth={width}
            >
              {(warningProps) => {
                const commonProps = {
                  isReply,
                  isSmallPreview,
                  containerWidth: width,
                  onClick: openGallery,
                  ...warningProps,
                  ...editProps,
                }

                return isSingleMedia ? (
                  <SingleMedia upload={attachments[0]} {...commonProps} />
                ) : (
                  <MultipleMedia attachments={attachments} {...commonProps} />
                )
              }}
            </ContentWarningWrapper>
          )}
        </MediaGallery>
      )}
    </div>
  )
}

export { MediaContent }
